import { api } from "./client";
import type {
  Message,
  Contact,
  Template,
  Staff,
  Device,
  PaginatedResponse,
  DashboardStats,
  CustomerSearchResult,
  BookingInfo,
  BookingSlot,
  SettingItem,
} from "@/types";

// Auth
export const authApi = {
  login: (username: string, password: string) =>
    api.post<{ ok: boolean; staffId: string | null; staffName: string; isAdmin: boolean }>("/auth/login", { username, password }),
  me: () => api.get<{ username: string; staffId: string | null; staffName: string; isAdmin: boolean }>("/auth/me"),
  logout: () => api.post<{ ok: boolean }>("/auth/logout", {}),
  googleLogin: (credential: string) =>
    api.post<{ ok: boolean; staffId: string | null; staffName: string; isAdmin: boolean }>("/auth/google", { credential }),
  getGoogleClientId: () =>
    api.get<{ clientId: string }>("/auth/google-client-id"),
};

// Messages
export const messagesApi = {
  list: (params: Record<string, string>) => {
    const qs = new URLSearchParams(params).toString();
    return api.get<PaginatedResponse<Message>>(`/messages?${qs}`);
  },
  get: (id: string) => api.get<Message>(`/messages/${id}`),
  cancel: (id: string) => api.delete<{ ok: boolean }>(`/messages/${id}`),
};

// Conversations
export const conversationsApi = {
  list: () => api.get<{ data: Contact[] }>("/conversations"),
  messages: (contactId: string, params?: Record<string, string>) => {
    const qs = params ? new URLSearchParams(params).toString() : "";
    return api.get<PaginatedResponse<Message>>(
      `/conversations/${contactId}?${qs}`,
    );
  },
};

// Staff (担当者)
export const staffApi = {
  list: () => api.get<{ data: Staff[] }>("/staff"),
  create: (data: { name: string; email?: string; bookingUrl: string; active?: boolean }) =>
    api.post<Staff>("/staff", data),
  update: (id: string, data: Partial<Staff>) =>
    api.put<Staff>(`/staff/${id}`, data),
  delete: (id: string) => api.delete<{ ok: boolean }>(`/staff/${id}`),
};

// Customers (Core DB)
export const customerApi = {
  search: (q: string) =>
    api.get<{ data: CustomerSearchResult[] }>(`/customers/search?q=${encodeURIComponent(q)}`),
};

// Booking (public)
export const bookingApi = {
  info: (token: string) =>
    api.get<{ booking: BookingInfo; availableSlots: BookingSlot[] }>(
      `/booking/info?t=${encodeURIComponent(token)}`,
    ),
  confirm: (data: { bookingId: string; slotDate: string; slotTime: string; traderPhone?: string; traderName?: string }) =>
    api.post<{ booking: BookingInfo }>("/booking/confirm", data),
};

// Send
export const sendApi = {
  send: (data: {
    to: string; body: string; templateId?: string; staffId?: string;
    customerId?: number; customerName?: string; companyName?: string;
  }) =>
    api.post<{ id: string; status: string; bookingId?: string }>("/send", data),
  bulkSend: (data: {
    recipients: Array<string | Record<string, string>>;
    templateId?: string;
    body?: string;
    variables?: Record<string, string>;
  }) => api.post<{ queued: number; messageIds: string[] }>("/bulk-send", data),
};

// Templates
export const templatesApi = {
  list: () => api.get<{ data: Template[] }>("/templates"),
  create: (data: Omit<Template, "id" | "createdAt" | "updatedAt">) =>
    api.post<Template>("/templates", data),
  update: (id: string, data: Partial<Template>) =>
    api.put<Template>(`/templates/${id}`, data),
  delete: (id: string) => api.delete<{ ok: boolean }>(`/templates/${id}`),
};

// Opt-outs
export const optOutsApi = {
  list: (q?: string) =>
    api.get<{ data: Contact[] }>(`/opt-outs${q ? `?q=${q}` : ""}`),
  add: (contactId: string) =>
    api.post<Contact>("/opt-outs", { contactId }),
  addByPhone: (phone: string) =>
    api.post<Contact>("/opt-outs", { phone }),
  remove: (contactId: string) =>
    api.delete<{ ok: boolean }>(`/opt-outs/${contactId}`),
};

// Devices
export const devicesApi = {
  list: () => api.get<{ data: Device[] }>("/devices"),
};

// Settings
export const settingsApi = {
  list: () => api.get<{ data: SettingItem[] }>("/settings"),
  update: (settings: Array<{ key: string; value: string }>) =>
    api.put<{ ok: boolean }>("/settings", { settings }),
};

// Dashboard
export const dashboardApi = {
  stats: (from?: string, to?: string) => {
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    return api.get<DashboardStats>(`/dashboard/stats?${params}`);
  },
};
